import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { motion } from "framer-motion";
import { Crown, Trophy, TrendingUp } from "lucide-react";
import WhaleLogo from "./WhaleLogo";
import { useAuth } from "../context/AuthContext";
import { useWallet } from "../hooks/useWallet";
import { API } from "../lib/constants";

const reveal = {
  initial: { y: 36, opacity: 0 },
  whileInView: { y: 0, opacity: 1 },
  viewport: { once: true, amount: 0.15 },
};

const fmt = (n) => (n ?? 0).toLocaleString("en-US");

const AVATAR = "h-9 w-9 rounded-full border-[3px] border-ocean-navy object-cover";

const Avatar = ({ user }) =>
  user?.picture ? (
    <img src={user.picture} alt="" referrerPolicy="no-referrer" className={AVATAR} />
  ) : (
    <span className="flex h-9 w-9 items-center justify-center rounded-full border-[3px] border-ocean-navy bg-ocean-bg2">
      <WhaleLogo className="h-6 w-6" />
    </span>
  );

const PODIUM = [
  { place: 1, bg: "bg-ocean-yellow", icon: Crown, height: "sm:pt-10" },
  { place: 2, bg: "bg-ocean-sky", icon: Trophy, height: "sm:pt-16" },
  { place: 3, bg: "bg-ocean-coral", icon: Trophy, height: "sm:pt-16" },
];

export default function Leaderboard() {
  const { user } = useAuth();
  const { wallet } = useWallet(user);
  const { data, isLoading } = useQuery({
    queryKey: ["leaderboard", wallet?.balance],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/leaderboard`, { withCredentials: true });
      return data;
    },
    refetchInterval: 60000,
  });

  const leaders = data?.leaders || [];
  const me = data?.me;

  return (
    <section
      id="holders"
      data-testid="leaderboard-section"
      className="relative py-24 sm:py-28"
    >
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <motion.div {...reveal} className="mx-auto max-w-2xl text-center">
          <p className="flex items-center justify-center gap-2 text-xs font-extrabold uppercase tracking-[0.3em] text-ocean-cyan">
            <TrendingUp className="h-4 w-4" />
            Holder Spotlight
          </p>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
            The biggest stacks in the pod.
          </h2>
          <p className="mt-3 text-base font-medium text-ocean-muted sm:text-lg">
            Claim your daily $BLUE, climb the wall of the pod.{" "}
            <span className="font-bold text-ocean-navy">
              Pod points for fun — not on-chain holdings.
            </span>
          </p>
        </motion.div>

        {isLoading ? (
          <div className="mt-12 flex justify-center">
            <span className="h-12 w-12 animate-spin rounded-full border-4 border-ocean-sky border-t-transparent" />
          </div>
        ) : leaders.length === 0 ? (
          <motion.div {...reveal} className="sticker mx-auto mt-12 max-w-lg rounded-3xl bg-white p-8 text-center">
            <WhaleLogo className="mx-auto h-14 w-14" />
            <p className="mt-4 font-display text-xl font-semibold">
              The pod is just warming up.
            </p>
            <p className="mt-2 text-sm font-medium text-ocean-muted">
              Sign in, claim your daily $BLUE and take the crown.
            </p>
          </motion.div>
        ) : (
          <>
            <div className="mt-12 grid gap-5 sm:grid-cols-3">
              {PODIUM.slice(0, Math.min(3, leaders.length)).map((p, i) => {
                const l = leaders[i];
                return (
                  <motion.div
                    key={l.rank}
                    data-testid={`leaderboard-podium-${l.rank}`}
                    {...reveal}
                    transition={{ ...reveal, delay: i * 0.1 }}
                    className={`sticker ${p.bg} ${p.height} relative rounded-3xl p-6 text-center ${
                      l.rank === 1 ? "sm:-order-1" : ""
                    }`}
                  >
                    <p.icon
                      className={`absolute -top-4 left-1/2 h-9 w-9 -translate-x-1/2 rounded-full border-[3px] border-ocean-navy p-1.5 ${
                        l.rank === 1 ? "bg-ocean-coral text-white" : "bg-white"
                      }`}
                    />
                    <div className="flex justify-center">
                      <Avatar user={l} />
                    </div>
                    <p className="mt-3 truncate font-display text-lg font-semibold" data-testid={`leaderboard-name-${l.rank}`}>
                      {l.name}
                    </p>
                    <p className="font-display text-2xl font-semibold" data-testid={`leaderboard-balance-${l.rank}`}>
                      {fmt(l.balance)} <span className="text-base">$BLUE</span>
                    </p>
                    <span className="sticker-sm mt-3 inline-block rounded-full bg-white px-3 py-0.5 text-xs font-extrabold uppercase tracking-widest">
                      {l.rank === 1 ? "Biggest whale" : `#${l.rank} in the pod`}
                    </span>
                  </motion.div>
                );
              })}
            </div>

            {leaders.length > 3 && (
              <div className="sticker mt-8 overflow-hidden rounded-3xl bg-white">
                {leaders.slice(3).map((l, i) => {
                  const isMe = user && me && me.name === l.name && me.rank === l.rank;
                  return (
                    <div
                      key={l.rank}
                      data-testid={`leaderboard-row-${l.rank}`}
                      className={`flex items-center gap-4 px-6 py-4 ${
                        i > 0 ? "border-t-[3px] border-ocean-navy/10" : ""
                      } ${isMe ? "bg-ocean-yellow/30" : ""}`}
                    >
                      <span className="font-display text-xl font-semibold text-ocean-navy/50">
                        {l.rank}
                      </span>
                      <Avatar user={l} />
                      <span className="min-w-0 flex-1 truncate font-display text-base font-semibold">
                        {l.name}
                        {isMe && (
                          <span className="sticker-sm ml-2 rounded-full bg-ocean-yellow px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-widest">
                            you
                          </span>
                        )}
                      </span>
                      <span className="font-display text-base font-semibold">
                        {fmt(l.balance)} $BLUE
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {user && me && me.rank > 3 && (
              <motion.p
                {...reveal}
                data-testid="leaderboard-me"
                className="sticker-sm mx-auto mt-6 w-fit rounded-full bg-ocean-navy px-5 py-2.5 font-display text-sm font-semibold text-white"
              >
                You're #{me.rank} with {fmt(me.balance)} $BLUE — keep claiming to climb.
              </motion.p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
